/* Classes: the schedule, and who was actually in the room.
 *
 * It sits ABOVE Leads and Members on the floor seats' nav: the floor comes before the
 * pipeline, and checking people in is its own job worked at its own pace.
 *
 * ONE HONEST LIMIT, STATED ON THE SCREEN. The gym runs a repeating weekly timetable
 * and the seed holds one day of it, so every day shows the same sessions. Attendance
 * is only writable for today, and past days show what was recorded.
 *
 * MEMBER CONTEXT IS THE POINT. A register that only counts heads answers the wrong
 * question. Every line beside a name is derived at read time from the record, by
 * @acrostic/model/register, and the thresholds live there so the bar across the top
 * and the rows underneath cannot drift apart.
 */
import { useState } from 'react';

import type { ISODate, Member } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { ContextGroup, ContextPerson } from '../data/contract.js';
import { NoneRow } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { Modal } from '../components/Modal.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';
import { Register } from './classes/Register.js';

/** Seven days ENDING today. Attendance is a record, so the useful direction is
 *  backwards: "who came on Tuesday" is answerable, "who will come on Thursday" is not. */
function lastSevenDays(today: string): { iso: string; label: string; num: number }[] {
  const out: { iso: string; label: string; num: number }[] = [];
  const [y, m, d] = today.split('-').map(Number);
  for (let i = 6; i >= 0; i--) {
    const dt = new Date(y!, m! - 1, d! - i, 12);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    out.push({ iso, label: dt.toLocaleDateString('en-US', { weekday: 'short' }), num: dt.getDate() });
  }
  return out;
}

export function Classes() {
  const source = useSource();
  /* TODAY IS THE GYM'S, and the source is the only thing that knows it. The browser
     knows where the LAPTOP is: at 5:30pm in California it was asking for a date the
     server had already rolled past, and the register came back as a closed day with no
     boxes to tick - every evening, which is when classes run. */
  const session = useAsync(() => source.getSession(), [source]);
  const today = session.data?.today ?? null;
  const [picked, setPicked] = useState<ISODate | null>(null);
  const date = picked ?? today;
  const setDate = setPicked;
  const [open, setOpen] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [group, setGroup] = useState<ContextGroup | null>(null);

  const { data, error } = useAsync(
    () => (date ? source.getSchedule(date) : Promise.resolve(null)), [source, date, epoch]);
  const days = today ? lastSevenDays(today) : [];

  return (
    <>
      <div className="daystrip">
        {days.map((d) => (
          <button
            key={d.iso} type="button"
            className={'day' + (d.iso === date ? ' on' : '')}
            onClick={() => setDate(d.iso)}
          >
            <span className="dw t-label-xsmall">{d.iso === today ? 'Today' : d.label}</span>
            <span className="dn t-label-large">{d.num}</span>
          </button>
        ))}
      </div>
      <p className="t-caption muted tight-above">
        The timetable repeats weekly. Attendance can only be recorded for today.
      </p>

      {error && <NoneRow>Could not read the day. {error.message}</NoneRow>}
      {!error && !data && <NoneRow>Reading the day…</NoneRow>}

      {data && (
        <>
          <section className="panel">
            <div className="eyebrow t-label-xsmall">
              Member context · {date === today ? 'everyone on today’s registers' : 'everyone in the room on ' + fmt.date(date)}
            </div>
            <div className="stats ctx-bar">
              {data.context.map((g) => (
                <button
                  key={g.key} type="button"
                  className={'stat ctx-stat' + (g.people.length ? '' : ' off')}
                  disabled={!g.people.length}
                  onClick={() => setGroup(g)}
                >
                  <div className="l t-caption">{g.label}</div>
                  {/* Formatted: a day's registers at a big gym carry hundreds. */}
                  <div className="v t-display-xsmall">{fmt.num(g.people.length)}</div>
                  {g.people.length ? (
                    <div className="names">
                      {g.people.slice(0, 3).map((p) => (
                        <span className="pill face t-caption" key={p.member.id}>
                          <Avatar name={p.member.name} photoUrl={p.member.photoUrl} size="sm" />
                          {p.member.name}
                        </span>
                      ))}
                      {g.people.length > 3 && (
                        <span className="pill t-caption" title={g.people.slice(3).map((p) => p.member.name).join(', ')}>
                          +{g.people.length - 3}
                        </span>
                      )}
                    </div>
                  ) : <div className="t-caption muted">{g.empty}</div>}
                </button>
              ))}
            </div>
          </section>

          <section className="panel">
            <table className="table">
              <thead>
                <tr className="t-label-small">
                  <th>Time</th><th>Class</th><th>Coach</th>
                  <th className="right">Attended</th><th>New · first 90 days</th><th />
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.cls.id} onClick={() => setOpen(s.cls.id)}>
                    <td className="t-body-small">{s.cls.time}</td>
                    <td className="t-label-large">{s.cls.name}</td>
                    <td className="t-body-small muted">{s.cls.instructor ?? 'Self-guided'}</td>
                    <td className="right t-body-small">{s.attended} / {s.cls.cap ?? '—'}</td>
                    <td><NewFaces people={s.newFaces} /></td>
                    <td className="right">
                      {/* A real button, not a span that looks like one. The row opens
                          the register too, but the control has to be reachable by
                          keyboard and findable by anything that looks for a button. */}
                      <button type="button" className="btn t-label-small"
                        onClick={(e) => { e.stopPropagation(); setOpen(s.cls.id); }}>Attendance</button>
                    </td>
                  </tr>
                ))}
                {!data.sessions.length && (
                  <tr><td colSpan={6}><NoneRow>No classes on the schedule.</NoneRow></td></tr>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}

      {open && date && (
        <Register
          classId={open} date={date}
          onClose={() => setOpen(null)}
          onWrote={() => setEpoch((n) => n + 1)}
        />
      )}

      {group && date && <GroupModal g={group} date={date} today={today} onClose={() => setGroup(null)} />}
    </>
  );
}

/** Names, then "+N", so the row stays one line. */
function NewFaces({ people }: { people: Member[] }) {
  if (!people.length) return <span className="muted t-body-small">—</span>;
  const shown = people.slice(0, 3);
  return (
    <span className="names">
      {shown.map((m) => (
        <span className="pill good face t-caption" key={m.id}>
          <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />{m.name}
        </span>
      ))}
      {people.length > shown.length && (
        <span className="pill t-caption" title={people.slice(3).map((m) => m.name).join(', ')}>
          +{people.length - shown.length}
        </span>
      )}
    </span>
  );
}

/* A count must open its names (SITE-RULES 11e), and the names have to carry the same
   context the register shows, or the bar is a different opinion from the rows. */
function GroupModal(
  { g, date, today, onClose }:
  { g: ContextGroup; date: string; today: string | null; onClose: () => void },
) {
  return (
    <Modal
      title={`${g.label} · ${date === today ? 'today' : fmt.date(date)}`}
      onClose={onClose}
      footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Done</button>}
    >
      <div className="rows">
        {g.people.map((p: ContextPerson) => (
          <button
            key={p.member.id} type="button" className="row clickable"
            onClick={() => { onClose(); go('/members/' + p.member.id); }}
          >
            <Avatar name={p.member.name} photoUrl={p.member.photoUrl} size="md" />
            <div className="who">
              <div className="n t-label-large">
                {p.member.name}
                {p.context.map((c) => (
                  <span className={'pill ' + c.tone + ' t-caption'} key={c.key} title={c.why}>{c.text}</span>
                ))}
              </div>
              <div className="s t-caption muted">{p.sessions.join(' · ')}</div>
            </div>
            <span className="t-label-medium muted">→</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
